import { useEffect, useState } from "react";
import { CheckCircle2, Lightbulb, Sparkles } from "lucide-react";
import type { useWorkspace } from "../hooks/useWorkspace";
import { PHONE_QUERY, useMediaQuery } from "../hooks/useMediaQuery";
import { interpolate, localizePurposeForLocale, t } from "../i18n";
import { ModalSheet } from "./ModalSheet";
import "./AssistantPanel.css";

type Workspace = ReturnType<typeof useWorkspace>;

export function AssistantPanel({ workspace }: { workspace: Workspace }) {
  const { state } = workspace;
  const [isOpen, setIsOpen] = useState(false);
  const isPhone = useMediaQuery(PHONE_QUERY);
  const selected = state.plan.find((slot) => slot.id === state.selectedSlotId);
  const purpose = localizePurposeForLocale(
    state.locale,
    selected?.purpose ?? "商品主圖",
  );
  const titleKey = {
    setup: "assistantSetupTitle",
    plan: "assistantPlanTitle",
    review: "assistantReviewTitle",
  } as const;
  const adviceKey = {
    setup: "assistantSetupAdvice",
    plan: "assistantPlanAdvice",
    review: "assistantReviewAdvice",
  } as const;
  const factLabels = {
    shape: t(state.locale, "productShape"),
    colours: t(state.locale, "productColour"),
    logo: t(state.locale, "brandMark"),
    packaging: t(state.locale, "packagingText"),
  };
  const title = t(state.locale, titleKey[state.stage]);
  const recommendation = interpolate(t(state.locale, adviceKey[state.stage]), {
    purpose,
  });
  useEffect(() => {
    if (!isPhone) setIsOpen(false);
  }, [isPhone]);

  const assistantLabel = t(state.locale, "assistant");

  const content = (
    <>
      <div className="assistant-orb">
        <Sparkles size={17} aria-hidden="true" />
      </div>
      <p className="eyebrow">{assistantLabel}</p>
      <h2>{title}</h2>
      <div className="assistant-section ai">
        <div className="assistant-section-title">
          <Lightbulb size={15} aria-hidden="true" />
          <strong>{t(state.locale, "aiSuggestion")}</strong>
        </div>
        <p>{recommendation}</p>
      </div>
      <div className="assistant-section facts">
        <div className="assistant-section-title">
          <CheckCircle2 size={15} aria-hidden="true" />
          <strong>{t(state.locale, "facts")}</strong>
        </div>
        {!state.productReady ? (
          <p>{t(state.locale, "photoRequired")}</p>
        ) : state.confirmedFacts.length ? (
          <ul>
            {state.confirmedFacts.map((fact) => (
              <li key={fact}>{factLabels[fact]}</li>
            ))}
          </ul>
        ) : (
          <p>{t(state.locale, "waitingFacts")}</p>
        )}
      </div>
    </>
  );

  return (
    <>
      <aside className="assistant-panel" aria-label={assistantLabel}>
        {!isPhone && content}
      </aside>
      <div className="assistant-mobile-access">
        <button
          className="assistant-mobile-trigger"
          type="button"
          aria-haspopup="dialog"
          aria-expanded={isPhone && isOpen}
          onClick={() => setIsOpen(true)}
        >
          <Sparkles size={20} aria-hidden="true" />
          {assistantLabel}
        </button>
      </div>
      <ModalSheet
        open={isPhone && isOpen}
        title={assistantLabel}
        closeLabel={t(state.locale, "close")}
        onClose={() => setIsOpen(false)}
        className="assistant-mobile-sheet"
        lang={state.locale}
      >
        {content}
      </ModalSheet>
    </>
  );
}
